import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Eye, ArrowLeft, RefreshCw, Plus, FileText, DollarSign, ShoppingCart, Clock, TrendingUp, Trash2, Edit3, MessageCircle, Heart } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // all | sale | donation | giveaway

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/orders`, {
        withCredentials: true,
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const updateOrderStatus = async (orderNumber, newStatus) => {
    try {
      await axios.put(`${API}/api/admin/orders/${orderNumber}`, 
        { status: newStatus },
        { withCredentials: true }
      );
      fetchOrders();
      fetchStats();
      if (selectedOrder?.order_number === orderNumber) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  // Mark order as Paid AND open WhatsApp with a confirmation message to the customer.
  // Phone digits-only is required for wa.me; we already validate before showing this button.
  const markPaidAndWhatsApp = async (order) => {
    const sanitizedPhone = (order.phone || "").replace(/\D/g, "");
    const firstName = (order.customer_name || "").trim().split(/\s+/)[0] || "there";
    const message = `Hi ${firstName}! 👋\n\n✅ Payment received for order #${order.order_number} — thank you!\n\nWe're preparing your order now and will message again as soon as it's ready for ${order.delivery_method === "pickup" ? "pickup" : "delivery"}.\n\n— FarmSmart Kids 🐄`;
    const waUrl = `https://wa.me/${sanitizedPhone}?text=${encodeURIComponent(message)}`;
    // Open WhatsApp first (in a new tab) so the popup blocker doesn't kill it during the async PUT.
    window.open(waUrl, "_blank", "noopener,noreferrer");
    await updateOrderStatus(order.order_number, "paid");
  };

  const deleteOrder = async (orderNumber) => {
    const confirmed = window.confirm(
      `Permanently delete order ${orderNumber}?\n\nThis cannot be undone. Use this only for tests / mistakes — don't use to refund a real customer.`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/api/admin/orders/${orderNumber}`, { withCredentials: true });
      setSelectedOrder(null);
      fetchOrders();
      fetchStats();
    } catch (error) {
      console.error("Error deleting order:", error);
      alert("Failed to delete the order. Please try again.");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending_payment":
        return "bg-[#E9C46A] text-[#1D3557]";
      case "paid":
        return "bg-[#2A9D8F] text-white";
      case "shipped":
        return "bg-[#457B9D] text-white";
      case "delivered":
        return "bg-[#2A9D8F] text-white";
      case "cancelled":
        return "bg-[#E63946] text-white";
      default:
        return "bg-[#1D3557] text-white";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending_payment":
        return "Pending Payment";
      case "paid":
        return "Paid";
      case "shipped":
        return "Shipped";
      case "delivered":
        return "Delivered";
      case "cancelled":
        return "Cancelled";
      default:
        return status;
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (statusFilter !== "all" && order.status !== statusFilter) return false;
    if (typeFilter !== "all" && (order.order_type || "sale") !== typeFilter) return false;
    return true;
  });

  const orderTypeMeta = (t) => {
    switch (t) {
      case "donation":
        return { label: "Donation", color: "bg-[#E63946] text-white" };
      case "giveaway":
        return { label: "Giveaway", color: "bg-[#E9C46A] text-[#1D3557]" };
      default:
        return null; // don't render badge for regular sales
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="text-[#1D3557] font-bold flex items-center gap-2 mb-2 hover:text-[#E63946]"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-black text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Order Management
            </h1>
            <p className="text-[#1D3557] font-medium">
              {orders.length} total orders
            </p>
          </div>
          <button
            onClick={() => { fetchOrders(); fetchStats(); }}
            className="btn-secondary text-base py-3 px-6 inline-flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
        </div>

        {/* Quick stats panel */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="stats-panel">
            <div className="tactile-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingCart className="w-5 h-5 text-[#2A9D8F]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#1D3557]/60">Orders</span>
              </div>
              <p className="text-2xl font-black text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }} data-testid="stat-orders">{stats.total_orders}</p>
              <p className="text-xs text-[#1D3557]/60 font-medium">{stats.total_books_sold} items</p>
            </div>
            <div className="tactile-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-5 h-5 text-[#2A9D8F]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#1D3557]/60">Revenue (paid)</span>
              </div>
              <p className="text-2xl font-black text-[#2A9D8F]" style={{ fontFamily: "'Fredoka', sans-serif" }} data-testid="stat-revenue">${stats.total_revenue.toFixed(2)}</p>
              <p className="text-xs text-[#1D3557]/60 font-medium">paid + shipped + delivered</p>
            </div>
            <div className="tactile-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-5 h-5 text-[#E9C46A]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#1D3557]/60">Pending</span>
              </div>
              <p className="text-2xl font-black text-[#E9C46A]" style={{ fontFamily: "'Fredoka', sans-serif" }} data-testid="stat-pending">${stats.pending_revenue.toFixed(2)}</p>
              <p className="text-xs text-[#1D3557]/60 font-medium">{stats.by_status.pending_payment || 0} order(s)</p>
            </div>
            <div className="tactile-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <Heart className="w-5 h-5 text-[#E63946]" fill="#E63946" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#1D3557]/60">Given away</span>
              </div>
              <p className="text-2xl font-black text-[#E63946]" style={{ fontFamily: "'Fredoka', sans-serif" }} data-testid="stat-given-away">{stats.items_given_away || 0}</p>
              <p className="text-xs text-[#1D3557]/60 font-medium">
                {stats.by_type?.donation || 0} donation{(stats.by_type?.donation || 0) === 1 ? "" : "s"} · {stats.by_type?.giveaway || 0} giveaway{(stats.by_type?.giveaway || 0) === 1 ? "" : "s"}
              </p>
            </div>
            <div className="tactile-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-[#457B9D]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#1D3557]/60">By source</span>
              </div>
              <div className="text-xs text-[#1D3557] font-bold space-y-0.5" data-testid="stat-sources">
                {Object.entries(stats.by_source).map(([k, v]) => (
                  <p key={k}>{k}: <span className="text-[#E63946]">{v}</span></p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Add manual order CTA */}
        <Link
          to="/admin/orders/new"
          className="btn-primary inline-flex items-center gap-2 mb-6"
          data-testid="new-manual-order-btn"
        >
          <Plus className="w-5 h-5" />
          New Manual Order
        </Link>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2 mb-3">
          {["all", "pending_payment", "paid", "shipped", "delivered", "cancelled"].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-xl font-bold text-sm border-3 border-[#1D3557] transition-all ${
                statusFilter === status
                  ? "bg-[#1D3557] text-white shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]"
                  : "bg-white text-[#1D3557] hover:shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]"
              }`}
              style={{ fontFamily: "'Fredoka', sans-serif" }}
            >
              {status === "all" ? "All Statuses" : getStatusLabel(status)}
            </button>
          ))}
        </div>

        {/* Type Filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="type-filter">
          {[
            { id: "all", label: "All Types" },
            { id: "sale", label: "Sales" },
            { id: "donation", label: "Donations" },
            { id: "giveaway", label: "Giveaways" }
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTypeFilter(t.id)}
              className={`px-3 py-1.5 rounded-lg font-bold text-xs border-2 border-[#1D3557] transition-all ${
                typeFilter === t.id
                  ? "bg-[#2A9D8F] text-white"
                  : "bg-white text-[#1D3557] hover:bg-[#2A9D8F]/10"
              }`}
              data-testid={`type-filter-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Orders List */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="tactile-card p-12 text-center">
                <Package className="w-16 h-16 text-[#1D3557]/30 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  No orders found
                </h3>
                <p className="text-[#1D3557]/70 font-medium">
                  {statusFilter === "all" ? "Orders will appear here when customers place them." : `No ${getStatusLabel(statusFilter).toLowerCase()} orders.`}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order) => {
                  const typeMeta = orderTypeMeta(order.order_type);
                  return (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`tactile-card p-4 cursor-pointer transition-all ${
                      selectedOrder?.id === order.id ? "border-[#E63946]" : ""
                    }`}
                    onClick={() => setSelectedOrder(order)}
                    data-testid={`order-row-${order.order_number}`}
                  >
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className="font-bold text-[#1D3557]">{order.order_number}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold border-2 border-[#1D3557] ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                          {typeMeta && (
                            <span className={`px-2 py-1 rounded-full text-xs font-bold border-2 border-[#1D3557] ${typeMeta.color}`} data-testid={`order-type-badge-${order.order_number}`}>
                              {typeMeta.label}
                            </span>
                          )}
                        </div>
                        <p className="text-[#1D3557] font-medium">{order.customer_name}</p>
                        <p className="text-[#1D3557]/70 text-sm font-medium">{order.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#E63946] text-lg">${order.total.toFixed(2)}</p>
                        <p className="text-[#1D3557]/70 text-sm font-medium">{order.quantity} item(s)</p>
                        <p className="text-[#1D3557]/50 text-xs font-medium">{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                  </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Order Details Panel */}
          <div className="lg:col-span-1">
            {selectedOrder ? (
              <div className="tactile-card p-6 sticky top-24">
                <h2 className="text-xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  Order Details
                </h2>

                <div className="space-y-4">
                  <div>
                    <p className="text-[#1D3557]/70 text-sm font-medium">Order Number</p>
                    <p className="font-bold text-[#1D3557]">{selectedOrder.order_number}</p>
                  </div>

                  <div>
                    <p className="text-[#1D3557]/70 text-sm font-medium">Status</p>
                    <select
                      value={selectedOrder.status}
                      onChange={(e) => updateOrderStatus(selectedOrder.order_number, e.target.value)}
                      className="select-field mt-1"
                      data-testid="status-select"
                    >
                      <option value="pending_payment">Pending Payment</option>
                      <option value="paid">Paid</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>

                    {/* Quick status buttons */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedOrder.status === "pending_payment" && (
                        <button
                          type="button"
                          onClick={() => updateOrderStatus(selectedOrder.order_number, "paid")}
                          className="px-3 py-1.5 rounded-lg bg-[#2A9D8F] hover:bg-[#1D3557] text-white text-xs font-bold transition-colors"
                          data-testid="mark-paid-btn"
                        >
                          ✓ Mark as Paid
                        </button>
                      )}
                      {selectedOrder.status === "pending_payment" && selectedOrder.phone && (
                        <button
                          type="button"
                          onClick={() => markPaidAndWhatsApp(selectedOrder)}
                          className="px-3 py-1.5 rounded-lg bg-[#25D366] hover:bg-[#1ebe5b] text-white text-xs font-bold transition-colors inline-flex items-center gap-1"
                          data-testid="mark-paid-whatsapp-btn"
                          title={`Marks Paid and opens WhatsApp to ${selectedOrder.phone}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          Mark Paid + WhatsApp reply
                        </button>
                      )}
                      {selectedOrder.status === "paid" && (
                        <button
                          type="button"
                          onClick={() => updateOrderStatus(selectedOrder.order_number, "shipped")}
                          className="px-3 py-1.5 rounded-lg bg-[#457B9D] hover:bg-[#1D3557] text-white text-xs font-bold transition-colors"
                          data-testid="mark-shipped-btn"
                        >
                          📦 Mark as Shipped
                        </button>
                      )}
                      {selectedOrder.status === "shipped" && (
                        <button
                          type="button"
                          onClick={() => updateOrderStatus(selectedOrder.order_number, "delivered")}
                          className="px-3 py-1.5 rounded-lg bg-[#2A9D8F] hover:bg-[#1D3557] text-white text-xs font-bold transition-colors"
                          data-testid="mark-delivered-btn"
                        >
                          ✅ Mark as Delivered
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border-t-2 border-[#1D3557]/10 pt-4">
                    <p className="text-[#1D3557]/70 text-sm font-medium mb-1">Customer</p>
                    <p className="font-bold text-[#1D3557]">{selectedOrder.customer_name}</p>
                    <p className="text-[#1D3557] font-medium">{selectedOrder.email}</p>
                    {selectedOrder.phone && (
                      <p className="text-[#1D3557] font-medium">{selectedOrder.phone}</p>
                    )}
                  </div>

                  {(selectedOrder.address?.line1 || selectedOrder.address?.city || selectedOrder.address?.country) && (
                    <div className="border-t-2 border-[#1D3557]/10 pt-4">
                      <p className="text-[#1D3557]/70 text-sm font-medium mb-1">Shipping Address</p>
                      <p className="text-[#1D3557] font-medium">
                        {selectedOrder.address?.line1}{selectedOrder.address?.line1 && <br />}
                        {selectedOrder.address?.line2 && <>{selectedOrder.address.line2}<br /></>}
                        {selectedOrder.address?.city && <>{selectedOrder.address.city}{selectedOrder.address?.state_province ? `, ${selectedOrder.address.state_province}` : ""}<br /></>}
                        {selectedOrder.address?.postal_code && <>{selectedOrder.address.postal_code}<br /></>}
                        {selectedOrder.address?.country && <strong>{selectedOrder.address.country}</strong>}
                      </p>
                    </div>
                  )}

                  <div className="border-t-2 border-[#1D3557]/10 pt-4">
                    <p className="text-[#1D3557]/70 text-sm font-medium mb-2">Items</p>
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      <ul className="space-y-1.5">
                        {selectedOrder.items.map((it, idx) => (
                          <li key={idx} className="flex items-start justify-between gap-2 text-sm" data-testid={`order-item-${idx}`}>
                            <span className="text-[#1D3557] font-medium leading-tight">
                              <span className="font-bold text-[#1D3557]/70">{it.quantity}×</span> {it.name}
                            </span>
                            <span className="text-[#1D3557] font-bold flex-shrink-0">${(it.line_total ?? it.unit_price * it.quantity).toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <>
                        <p className="text-[#1D3557] font-medium">{selectedOrder.product_name}</p>
                        <p className="text-[#1D3557]/70 font-medium">Qty: {selectedOrder.quantity}</p>
                      </>
                    )}
                  </div>

                  <div className="border-t-2 border-[#1D3557]/10 pt-4 space-y-2">
                    <div className="flex justify-between text-[#1D3557] font-medium">
                      <span>Subtotal</span>
                      <span>${selectedOrder.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#1D3557] font-medium">
                      <span>Shipping</span>
                      <span>${selectedOrder.shipping.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#1D3557] font-bold text-lg pt-2 border-t-2 border-[#1D3557]/10">
                      <span>Total</span>
                      <span className="text-[#E63946]">${selectedOrder.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {selectedOrder.notes && (
                    <div className="border-t-2 border-[#1D3557]/10 pt-4">
                      <p className="text-[#1D3557]/70 text-sm font-medium mb-1">Notes</p>
                      <p className="text-[#1D3557] font-medium bg-[#E9C46A]/10 p-3 rounded-xl">
                        {selectedOrder.notes}
                      </p>
                    </div>
                  )}

                  <div className="border-t-2 border-[#1D3557]/10 pt-4">
                    <p className="text-[#1D3557]/70 text-sm font-medium">Order Date</p>
                    <p className="text-[#1D3557] font-medium">{formatDate(selectedOrder.created_at)}</p>
                  </div>

                  <div className="pt-2 space-y-2">
                    <Link
                      to={`/admin/invoice/${selectedOrder.order_number}`}
                      className="btn-primary w-full inline-flex items-center justify-center gap-2"
                      data-testid="view-invoice-btn"
                    >
                      <FileText className="w-5 h-5" />
                      Open Invoice
                    </Link>
                    <Link
                      to={`/admin/orders/${selectedOrder.order_number}/edit`}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 border-[#1D3557] text-[#1D3557] font-bold hover:bg-[#1D3557] hover:text-white transition-colors text-sm"
                      data-testid="edit-order-btn"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit order (items, customer, totals)
                    </Link>
                    <button
                      type="button"
                      onClick={() => deleteOrder(selectedOrder.order_number)}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 border-[#E63946] text-[#E63946] font-bold hover:bg-[#E63946] hover:text-white transition-colors text-sm"
                      data-testid="delete-order-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete order (tests / mistakes only)
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="tactile-card p-6 text-center">
                <Eye className="w-12 h-12 text-[#1D3557]/30 mx-auto mb-4" />
                <p className="text-[#1D3557]/70 font-medium">
                  Select an order to view details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrdersPage;
